/**
 * Which Touch detail rows survive the page's filters, on one of two bases.
 *
 *  - 'touch' — the touches a person MADE, by the date they made them. The Ledger's
 *    Touch detail, and the page's own default.
 *  - 'claim' — every touch on the claims a person OWNS that are resolved, by the month
 *    each claim RESOLVED (billed any month; review #397 — by the month it was billed
 *    in files built before), whoever made the touch and whenever. This is what
 *    `resolvedTouches` sums (MEASURE-RULES §5), so the person page, the team drawer and
 *    the Touch profile tie to it.
 *
 * They are not two views of one set. In the real data only 2% of the touches on a
 * resolved claim fall in its billed month and a third are by someone other than its
 * owner, so a person whose August claims were worked in September has touches on the
 * profile and none made in August (review #23: "when i drill through to touches it
 * says 0 data even though on the touch profile it shows she has touches").
 */
import type { TouchDetailData } from './touchDetail'

export type TouchBasis = 'touch' | 'claim'

/**
 * The month column the claim basis reads: `rm`, the month the claim resolved, or `bm`,
 * its billed month, on a file built before review #397 — each file's own, which is
 * the basis the bundle built beside it counts. Null without the claim link (files
 * built before 2026-10-02).
 */
export function claimMonth(data: TouchDetailData): { key: 'rm' | 'bm'; dict: string[]; col: number[] } | null {
  const { dict, cols } = data
  if (!dict.o || !cols.o || !cols.ci) return null
  if (dict.rm && cols.rm) return { key: 'rm', dict: dict.rm, col: cols.rm }
  if (dict.bm && cols.bm) return { key: 'bm', dict: dict.bm, col: cols.bm }
  return null
}

/** Whether the file carries the claim link; files built before 2026-10-02 do not. */
export function hasClaimBasis(data: TouchDetailData): boolean {
  return claimMonth(data) !== null
}

export interface TouchSelection {
  basis: TouchBasis
  /** The top bar's months, "YYYY-MM". */
  months: ReadonlySet<string>
  /** The people the filters show. */
  inScope: ReadonlySet<string>
  /** The same with Employee status set to both; see HiddenByStatus. */
  inAnyStatus: ReadonlySet<string>
  /** A team handed over by a drill, or null. */
  team: ReadonlySet<string> | null
  /**
   * Picked dropdowns: a column and the dictionary indexes it may hold — one, or every
   * blank spelling for an "Unknown" (data/blankValue.ts, review #177).
   */
  picks: readonly { col: readonly number[]; want: ReadonlySet<number> }[]
  /** The search, or null. */
  hit: ((i: number) => boolean) | null
}

/**
 * The surviving row indexes, how many the status control alone hides, and — on the
 * claim basis — how many distinct claims they are on, to set against the profile's
 * touched claims.
 */
export function selectTouches(
  data: TouchDetailData,
  sel: TouchSelection,
): { rows: number[]; hidden: number; claims: number | null } {
  const { dict, cols } = data
  const cm = sel.basis === 'claim' ? claimMonth(data) : null
  const byClaim = cm !== null
  // Whose row it is and which month it is in: the toucher and touch date, or the
  // claim's owner and the month it counts in. Months are judged once per dictionary entry.
  const who = byClaim ? dict.o! : dict.e
  const whoCol = byClaim ? cols.o! : cols.e
  const inMonths = (cm ? cm.dict : dict.d).map((d) => d !== '' && sel.months.has(d.slice(0, 7)))
  const monthCol = cm ? cm.col : cols.d

  const rows: number[] = []
  const claims = new Set<number>()
  let hidden = 0
  for (let i = 0; i < data.n; i++) {
    const owner = who[whoCol[i]!]!
    if (!sel.inAnyStatus.has(owner)) continue
    if (sel.team && !sel.team.has(owner)) continue
    if (!inMonths[monthCol[i]!]) continue
    if (sel.picks.some((p) => !p.want.has(p.col[i]!))) continue
    if (sel.hit && !sel.hit(i)) continue
    if (sel.inScope.has(owner)) {
      rows.push(i)
      if (byClaim) claims.add(cols.ci![i]!)
    } else hidden++
  }
  return { rows, hidden, claims: byClaim ? claims.size : null }
}

/**
 * One claim's touches: every touch row on its visit and payer, any date, by anyone —
 * Claim detail's Touches column and the Touch detail it opens (review #165). Claim
 * detail's rows carry no claim ID, so the claim is its visit + payer, the key the API
 * already finds a touch's claim weight by (api/shared/claimWeights.ts weighTouches).
 *
 * A row is a touch ACTION, as everywhere on the touch-date basis, so a touch with two
 * actions counts twice here and shows twice there: the count is the rows it opens.
 */
export const claimKey = (visit: string, payer: string) => `${visit}\u0001${payer.trim()}`

/** Touch rows by claimKey, built once per file. A blank visit is no claim. */
export function touchesByClaim(data: TouchDetailData): Map<string, number[]> {
  const out = new Map<string, number[]>()
  const { dict, cols } = data
  for (let i = 0; i < data.n; i++) {
    const v = cols.v[i]!
    if (!v) continue
    const k = claimKey(v, dict.pn[cols.pn[i]!] ?? '')
    const rows = out.get(k)
    if (rows) rows.push(i)
    else out.set(k, [i])
  }
  return out
}
